import React, { useEffect, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import compose from 'lodash/fp/compose';
import { Responsive, WidthProvider } from 'react-grid-layout';
import LazyLoad from 'react-lazyload';
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { ClickAwayListener } from '@mui/material';
import Icon from 'components/Icon';
import { withTranslation, withUserPreferences } from 'components/hoc';
import { RadialSeparators } from 'components/Dashboard/Home/utils';
import { getNotificationsThunk } from 'components/Dashboard/thunks';
import { deleteNoificationsService } from 'components/Dashboard/services';
import ExpenseChart from 'components/Dashboard/Home/Elements/Charts/ExpenseChart';
import AssetsVsLiabilities from 'components/Dashboard/Home/Elements/Charts/AssetsVsLiabilities';
import IncomeVsExpenditure from 'components/Dashboard/Home/Elements/Charts/IncomeVsExpenditure';
import IncomeExpenseBarChart from 'components/Dashboard/Home/Elements/Charts/IncomeVsExpenseBarChart';
import AssetLiabilityLineChart from 'components/Dashboard/Home/Elements/Charts/AssetLiabilityLineChart';
import styles from './gridLayoutStyles.module.css';
import Top5contractsChart from '../Charts/Top5contractsChart';
import { convertFromBytes, convertToBytes, MAX_STORAGE_SIZE } from 'utils';

const ResponsiveGridLayout = WidthProvider(Responsive);

const GRID_LAYOUT = 'grid-layout-v3.0.5';

const layout = [
  { i: 'Email', x: 0, y: 0, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },
  { i: 'Liabilities', x: 1, y: 0, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },
  { i: 'Assets', x: 2, y: 0, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },
  { i: 'Documents', x: 3, y: 0, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },
  { i: 'contracts_document_notification', x: 4, y: 0, w: 2, h: 1.547, minH: 1.547, maxH: 1.547 },

  { i: 'Space_group', x: 0, y: 0.51, w: 2, h: 1.035, minH: 1.035, maxH: 1.035 },
  { i: 'Top_5_contracts', x: 2, y: 0.51, w: 2, h: 1.035, minH: 1.035, maxH: 1.035 },

  { i: 'Expenditure_vs_income', x: 0, y: 1.53, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },
  { i: 'Liabilities_VS_assets', x: 1, y: 1.53, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },
  { i: 'Storage', x: 2, y: 1.53, w: 1, h: 0.51, minH: 0.51, maxH: 0.51 },

  { i: 'Donut_chart', x: 4, y: 1.53, w: 2, h: 1.547, minH: 1.547, maxH: 1.547 },
  { i: 'Line_chart', x: 0, y: 1.547, w: 4, h: 1.547, minH: 1.547, maxH: 1.547 },
  { i: 'Asset_liability_Line_chart', x: 0, y: 1.557, w: 4, h: 1.547, minH: 1.547, maxH: 1.547 },
];

const showDefaultElements = {
  Email: true,
  Liabilities: true,
  Assets: true,
  Documents: true,
  Space_group: true,
  Storage: true,
  Top_5_contracts: true,
  contracts_document_notification: true,
  Expenditure_vs_income: true,
  Liabilities_VS_assets: true,
  Line_chart: true,
  Donut_chart: true,
  Asset_liability_Line_chart: true,
};

const Grids = ({
  storageCounter,
  notifications,
  dashboard_summary,
  tr,
  fd,
  fn,
  dateFormat,
  userPreLang,
  ...props
}) => {
  const [showSetting, setShowSetting] = useState(false);
  const [showElements, setShowElements] = useState(showDefaultElements);
  const [removedElement, setRemovedElement] = useState([]);
  const dispatch = useDispatch();

  // Helper function to remove duplicate layouts and keep the height bounds
  // valid: react-grid-layout defaults minH to 1, which is larger than the
  // fractional heights these cards use, so it must be pinned to the item height.
  const normalizeLayouts = (layouts) => {
    if (!Array.isArray(layouts)) return [];
    const seen = new Set();
    return layouts
      .filter((el) => el?.i && !seen.has(el.i) && seen.add(el.i))
      .map((el) =>
        typeof el.h === 'number'
          ? { ...el, minH: Math.min(el.h, typeof el.maxH === 'number' ? el.maxH : el.h) }
          : el,
      );
  };

  const [layoutState, setLayoutState] = useState(() => {
    const savedLayouts = localStorage.getItem(GRID_LAYOUT);
    if (savedLayouts) {
      try {
        const parsed = JSON.parse(savedLayouts);
        // Remove duplicates based on 'i' property
        return normalizeLayouts(parsed);
      } catch (err) {
        return normalizeLayouts(layout);
      }
    }
    return normalizeLayouts(layout);
  });

  const used_storage_space = convertToBytes(storageCounter);
  const percentage = (used_storage_space / MAX_STORAGE_SIZE) * 100;
  const isOverLimit = percentage > 90;

  const isNotificationExpired = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    // Reset time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  // Filter notifications to exclude expired ones
  const activeNotifications =
    notifications?.filter(
      (notification) => !isNotificationExpired(notification.due_date),
    ) || [];

  useEffect(() => {
    const saved = localStorage.getItem(GRID_LAYOUT);
    if (saved) {
      try {
        setLayoutState(normalizeLayouts(JSON.parse(saved)));
      } catch (error) {
        localStorage.setItem(GRID_LAYOUT, JSON.stringify(layout));
        setLayoutState(normalizeLayouts(layout));
      }
    } else {
      localStorage.setItem(GRID_LAYOUT, JSON.stringify(layout));
    }
  }, []);

  useEffect(() => {
    if (!removedElement || removedElement.length === 0) return;

    const savedLayouts = localStorage.getItem(GRID_LAYOUT);
    try {
      const parsedSavedLayouts = JSON.parse(savedLayouts) || [];
      const filteredLayout = parsedSavedLayouts.filter(
        (el) => !removedElement.includes(el.i),
      );
      const uniqueFilteredLayout = normalizeLayouts(filteredLayout);
      localStorage.setItem(GRID_LAYOUT, JSON.stringify(uniqueFilteredLayout));
      setLayoutState(uniqueFilteredLayout);
    } catch (err) {
      console.error('Error filtering layouts:', err.message);
    }
  }, [removedElement]);

  useEffect(() => {
    handleShowElements();
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(GRID_LAYOUT);
      if (saved) {
        setLayoutState(normalizeLayouts(JSON.parse(saved)));
      }
      handleSetDashoardElements();
    } catch (err) {
      console.error('Error in showElements effect:', err.message);
    }
  }, [showElements]);

  const handleShowElements = () => {
    const showElementsStorage = localStorage.getItem('show-elements');
    const RemovedElementsStorage = localStorage.getItem('removed-elements');

    if (showElements === null && showElementsStorage === null) {
      setShowElements(showDefaultElements);
      localStorage.setItem('show-elements', JSON.stringify(showDefaultElements));
      localStorage.setItem('removed-elements', JSON.stringify([]));
    } else {
      try {
        if (showElementsStorage) {
          setShowElements(JSON.parse(showElementsStorage));
        }
        if (RemovedElementsStorage) {
          setRemovedElement(JSON.parse(RemovedElementsStorage));
        }
      } catch (err) {
        console.error('Error parsing show elements:', err.message);
        setShowElements(showDefaultElements);
        setRemovedElement([]);
      }
    }
  };

  const handleSetDashoardElements = () => {
    if (removedElement !== null && showElements !== null) {
      localStorage.setItem('removed-elements', JSON.stringify(removedElement));
      localStorage.setItem('show-elements', JSON.stringify(showElements));
    }
  };

  const handleDeleteNotification = async (notification_id) => {
    try {
      await deleteNoificationsService(notification_id);
      dispatch(getNotificationsThunk());
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  };

  React.useEffect(() => {
    const savedLayouts = localStorage.getItem(GRID_LAYOUT);
    if (!savedLayouts) {
      const uniqueLayouts = normalizeLayouts(layoutState);
      localStorage.setItem(GRID_LAYOUT, JSON.stringify(uniqueLayouts));
    }
  }, [layoutState]);

  const handleLayoutChange = useMemo(
    () =>
      debounce((layouts) => {
        const uniqueLayouts = normalizeLayouts(layouts);
        setLayoutState(uniqueLayouts);
        localStorage.setItem(GRID_LAYOUT, JSON.stringify(uniqueLayouts));
      }, 300),
    [],
  );

  const handleOnChangeElements = ({ target: { checked, value } }) => {
    const newShowElements = { ...showElements, [value]: checked };
    setShowElements(newShowElements);

    if (!checked) {
      // Remove element
      const newRemovedElements = [...(removedElement || []), value];
      setRemovedElement(newRemovedElements);

      const savedLayouts = localStorage.getItem(GRID_LAYOUT);
      try {
        const parsedLayouts = JSON.parse(savedLayouts) || [];
        const updatedLayouts = parsedLayouts.filter((layout) => layout.i !== value);
        const uniqueUpdatedLayouts = normalizeLayouts(updatedLayouts);
        setLayoutState(uniqueUpdatedLayouts);
        localStorage.setItem(GRID_LAYOUT, JSON.stringify(uniqueUpdatedLayouts));
      } catch (err) {
        console.error('Error removing element:', err.message);
      }
    } else {
      // Add element
      const newRemovedElements = (removedElement || []).filter(
        (item) => item !== value,
      );
      setRemovedElement(newRemovedElements);

      // Get the new item to add from the original layout
      const itemToAdd = layout.find((item) => item.i === value);
      if (itemToAdd) {
        // Check if item already exists in layoutState
        const itemExists = layoutState.some((item) => item.i === value);
        if (!itemExists) {
          const newLayoutState = [...layoutState, itemToAdd];
          const uniqueNewLayoutState = normalizeLayouts(newLayoutState);
          setLayoutState(uniqueNewLayoutState);
          localStorage.setItem(GRID_LAYOUT, JSON.stringify(uniqueNewLayoutState));
        }
      }
    }
  };

  // Simplified remove handlers using a generic function
  const createRemoveHandler = (elementId) => () => {
    const newRemovedElements = [...(removedElement || []), elementId];
    setRemovedElement(newRemovedElements);
    setShowElements({ ...showElements, [elementId]: false });
  };

  const handleRemoveEmail = createRemoveHandler('Email');
  const handleRemoveLiabilities = createRemoveHandler('Liabilities');
  const handleRemoveAssets = createRemoveHandler('Assets');
  const handleRemoveDocuments = createRemoveHandler('Documents');
  const handleRemoveSpaceGroup = createRemoveHandler('Space_group');
  const handleRemoveStorage = createRemoveHandler('Storage');
  const handleRemoveTop5contracts = createRemoveHandler('Top_5_contracts');
  const handleRemoveContractsDocumentNotification = createRemoveHandler(
    'contracts_document_notification',
  );
  const handleRemoveExpenditureVsincome = createRemoveHandler(
    'Expenditure_vs_income',
  );
  const handleRemoveLiabilitiesVsassets = createRemoveHandler(
    'Liabilities_VS_assets',
  );
  const handleRemoveLineChart = createRemoveHandler('Line_chart');
  const handleRemoveDonutChart = createRemoveHandler('Donut_chart');
  const handleRemoveAssetLiabilityLineChart = createRemoveHandler(
    'Asset_liability_Line_chart',
  );

  const translateDocumentNotificationString = (str) => {
    try {
      // Get document name, concerned person, and date from the string
      const documentNameMatch = str.match(/"([^"]+)"/);
      const documentName = documentNameMatch ? documentNameMatch[1] : '';

      // Get concerned person (between "of"/"von" and "will"/"wird")
      const concernedPersonMatch = str.match(
        /(?:of|von)\s+([^,.]+)(?=\s+(?:will get invalid|wird zum))/i,
      );
      const concernedPerson = concernedPersonMatch
        ? concernedPersonMatch[1].trim()
        : 'unknown person';

      // Find date in the string
      const dateMatches =
        str.match(/([A-Za-z]+ \d{1,2}, \d{4}|\d{4}-\d{2}-\d{2})/) || [];

      // Format date using fd
      const formattedDate = dateMatches[0]
        ? (() => {
            try {
              const dateObj = new Date(dateMatches[0]);
              return !isNaN(dateObj.getTime()) ? fd(dateObj) : dateMatches[0];
            } catch {
              return dateMatches[0];
            }
          })()
        : '';

      // Return translated string based on language
      return userPreLang === 'deu'
        ? `Das Dokument "${documentName}" von ${concernedPerson} wird zum ${formattedDate} ungültig. Falls notwendig, beantrage jetzt ein neues.`
        : `The document "${documentName}" of ${concernedPerson} will get invalid on ${formattedDate}. If necessary, please apply for a new one.`;
    } catch (error) {
      console.error('Error processing document notification:', error);
      return str;
    }
  };

  const translateContractNotificationString = (str) => {
    try {
      // Extract contract name and dates from the string
      const contractNameMatch = str.match(/"([^"]+)"/);
      const contractName = contractNameMatch ? contractNameMatch[1] : '';

      // Find all dates in the string (could be end_date and notice_deadline)
      const dateMatches = str.match(/([A-Za-z]+ \d{1,2}, \d{4})/g) || [];

      // Format dates using fd
      const formattedDates = dateMatches.map((dateStr) => {
        try {
          const dateObj = new Date(dateStr);
          return !isNaN(dateObj.getTime()) ? fd(dateObj) : dateStr;
        } catch {
          return dateStr;
        }
      });

      // Determine which type of message we're dealing with
      const isRuntimeMessage = str.includes('runtime') || str.includes('Laufzeit');
      const hasNoticeDeadline = str.includes('until') || str.includes('bis zum');

      // Get translations based on message type
      if (isRuntimeMessage && hasNoticeDeadline) {
        // Runtime with notice deadline
        const [endDate, noticeDeadline] = formattedDates;
        return userPreLang === 'deu'
          ? `Die Laufzeit Deines Vertrages "${contractName}" endet zum ${endDate}. Wenn Du ihn nicht automatisch verlängern lassen möchtest, solltest Du ihn bis zum ${noticeDeadline} beenden.`
          : `The runtime of your contract "${contractName}" will end at ${endDate}. If you don't want it to be renewed automatically, you should notify your service provider latest until ${noticeDeadline}.`;
      } else {
        const [endDate] = formattedDates;
        return userPreLang === 'deu'
          ? `Dein Vertrag "${contractName}" wird zum ${endDate} enden. Falls erforderlich, schließe jetzt einen neuen ab.`
          : `Your contract "${contractName}" will end at ${endDate}. If necessary, please take care for a new one.`;
      }
    } catch (error) {
      console.error('Error processing contract notification:', error);
      return str;
    }
  };

  const createElement = (element) => {
    const cardName =
      element?.i === 'Email'
        ? 'Email'
        : element.i === 'Liabilities'
        ? tr('Liabilities')
        : element.i === 'Assets'
        ? tr('Assets')
        : element.i === 'Documents'
        ? tr('Documents')
        : element.i === 'Space_group'
        ? tr('Space Group')
        : element.i === 'Storage'
        ? tr('Storage Space')
        : element.i === 'Top_5_contracts'
        ? tr('Contracts (cost per month)')
        : element.i === 'contracts_document_notification'
        ? tr('Notifications')
        : element.i === 'Expenditure_vs_income'
        ? tr('Income vs. Expenses')
        : element.i === 'Liabilities_VS_assets'
        ? tr('Assets vs. Liabilities')
        : element.i === 'Line_chart'
        ? tr('Income & Expenses Line Chart')
        : element.i === 'Donut_chart'
        ? tr('Donut Chart')
        : element.i === 'Asset_liability_Line_chart'
        ? tr('assets & liabilities line chart')
        : null;

    return (
      <div
        className={styles['grid_cardContainer']}
        key={element.i}
        data-grid={element}
      >
        <div className={styles['grid_cardtop']}>
          <h4 className={styles['grid_cardTitle']}> {cardName} </h4>

          {element.i === 'Email' && (
            <div onClick={handleRemoveEmail}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Liabilities' && (
            <div onClick={handleRemoveLiabilities}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Assets' && (
            <div onClick={handleRemoveAssets}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Documents' && (
            <div onClick={handleRemoveDocuments}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Space_group' && (
            <div onClick={handleRemoveSpaceGroup}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Storage' && (
            <div onClick={handleRemoveStorage}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Top_5_contracts' && (
            <div onClick={handleRemoveTop5contracts}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'contracts_document_notification' && (
            <div onClick={handleRemoveContractsDocumentNotification}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Expenditure_vs_income' && (
            <div onClick={handleRemoveExpenditureVsincome}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Liabilities_VS_assets' && (
            <div onClick={handleRemoveLiabilitiesVsassets}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Line_chart' && (
            <div onClick={handleRemoveLineChart}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Donut_chart' && (
            <div onClick={handleRemoveDonutChart}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}

          {element.i === 'Asset_liability_Line_chart' && (
            <div onClick={handleRemoveAssetLiabilityLineChart}>
              <Icon icon="cancel_dash" width={20} height={20} />
            </div>
          )}
        </div>

        <div className={styles['grid_cardBottomContainer']}>
          <div className={styles['cardbottom']}>
            <>
              {element.i === 'Email' && (
                <Icon icon={'email'} width={48} height={48} />
              )}

              {element.i === 'Liabilities' && (
                <Icon icon={'liabilities'} width={48} height={48} />
              )}

              {element.i === 'Assets' && (
                <Icon icon={'assets'} width={48} height={48} />
              )}

              {element.i === 'Documents' && (
                <Icon icon={'documents'} width={48} height={48} />
              )}

              {element.i === 'Storage' && (
                <div style={{ width: 70, height: 70 }}>
                  <CircularProgressbarWithChildren
                    value={used_storage_space}
                    maxValue={MAX_STORAGE_SIZE}
                    text={`${percentage.toFixed(1)}%`}
                    strokeWidth={10}
                    styles={buildStyles({
                      strokeLinecap: 'butt',
                      textColor: isOverLimit ? '#e74c3c' : undefined,
                      pathColor: isOverLimit ? '#e74c3c' : undefined,
                    })}
                  >
                    <RadialSeparators
                      count={12}
                      style={{
                        background: '#fff',
                        width: '2px',
                        // This needs to be equal to props.strokeWidth
                        height: `${10}%`,
                      }}
                    />
                  </CircularProgressbarWithChildren>
                </div>
              )}

              <div className={styles['grid_cardCounter']}>
                {element.i === 'Email' && (
                  <>
                    <div className={styles['grid_emailUnread']}>
                      {dashboard_summary?.total_unread_mails} {tr('unread')}
                    </div>

                    <div className={styles['grid_emailRead']}>
                      {dashboard_summary?.total_read_mails} {tr('read')}
                    </div>
                  </>
                )}

                {element.i === 'Liabilities' && (
                  <>
                    {fn(dashboard_summary?.total_liability_value)?.length > 4 ? (
                      <div className={styles['grid_counter-long']}>
                        {fn(dashboard_summary?.total_liability_value)}
                      </div>
                    ) : (
                      <div className={styles['grid_counter']}>
                        {fn(dashboard_summary?.total_liability_value)}
                      </div>
                    )}

                    <div className={styles['grid_unit']}>
                      {dashboard_summary?.space_currency}
                    </div>
                  </>
                )}

                {element.i === 'Assets' && (
                  <>
                    {fn(dashboard_summary?.total_asset_value)?.length > 4 ? (
                      <div className={styles['grid_counter-long']}>
                        {fn(dashboard_summary?.total_asset_value)}
                      </div>
                    ) : (
                      <div className={styles['grid_counter']}>
                        {fn(dashboard_summary?.total_asset_value)}
                      </div>
                    )}

                    <div className={styles['grid_unit']}>
                      {dashboard_summary?.space_currency}
                    </div>
                  </>
                )}

                {element.i === 'Documents' && (
                  <>
                    {dashboard_summary?.total_documents?.toString().length > 4 ? (
                      <div className={styles['grid_counter-long']}>
                        {dashboard_summary?.total_documents}
                      </div>
                    ) : (
                      <div className={styles['grid_counter']}>
                        {dashboard_summary?.total_documents}
                      </div>
                    )}

                    <div className={styles['grid_unit']}>{'DOCS'}</div>
                  </>
                )}

                {element.i === 'Storage' && (
                  <>
                    <div className={styles['grid_storage']}>
                      <span style={{ color: isOverLimit ? '#e74c3c' : undefined }}>
                        {storageCounter}
                      </span>{' '}
                      <small>{tr('OF')}</small>
                    </div>

                    <div className={styles['grid_storage']}>
                      {convertFromBytes(MAX_STORAGE_SIZE)}
                      <small>
                        {` `}
                        {tr('IN USE')}
                      </small>
                    </div>
                  </>
                )}
              </div>
            </>

            {element.i === 'Space_group' && (
              <div className={styles['grid_space_group']}>
                {dashboard_summary?.group_members?.length !== 0 ? (
                  dashboard_summary?.group_members?.map((person) =>
                    person?.person_picture ? (
                      <img
                        key={person?.id}
                        src={person?.person_picture}
                        width={60}
                        height={60}
                        className={styles['grid_space_group_image']}
                        alt={`Person ${person.id}`}
                      />
                    ) : (
                      <div
                        key={person?.id}
                        className={styles['grid_space_group_image']}
                      >
                        <Icon icon="space_group" width={60} height={60} />
                      </div>
                    ),
                  )
                ) : (
                  <div className={styles['infoEmptyState']}>
                    <Icon icon={'people'} width={80} height={80} />

                    <p className={styles['infoEmptyStateText']}>
                      {tr('Please assign a person group to this space')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {element.i === 'Top_5_contracts' && (
              <div className={styles['grid_graph_top5Contract']}>
                {Boolean(dashboard_summary?.top_5_contracts?.length) ? (
                  <Top5contractsChart data={dashboard_summary.top_5_contracts} />
                ) : (
                  <div className={styles['infoEmptyState']}>
                    <Icon icon={'horizontalChart'} width={150} height={150} />
                    <p className={styles['infoEmptyStateText']}>
                      {tr('No contracts yet!')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {element.i === 'contracts_document_notification' && (
              <div className={styles['grid_notification']}>
                {notifications?.length !== 0 ? (
                  <ul>
                    {notifications?.map((notification) => (
                      <li key={notification.id}>
                        {notification.document
                          ? translateDocumentNotificationString(notification.message)
                          : translateContractNotificationString(
                              notification.message,
                            )}
                        <span
                          onClick={() => handleDeleteNotification(notification.id)}
                        >
                          <Icon icon="cancel_2" width={8} height={8} />
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className={styles['infoEmptyState']}>
                    <Icon icon={'notificationSlash'} width={80} height={80} />

                    <p className={styles['infoEmptyStateText']}>
                      {tr('No notifications yet!')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {element.i === 'Liabilities_VS_assets' && (
            <div className={styles['grid_cardBottomChart']}>
              {!Boolean(Number(dashboard_summary?.asset_vs_liability?.assets)) &&
              !Boolean(Number(dashboard_summary?.asset_vs_liability?.liability_)) ? (
                <div className={styles['infoEmptyState']}>
                  <Icon icon={'barChart'} width={40} height={40} />
                  <p className={styles['infoEmptyStateText']}>
                    {tr('No entries yet!')}
                  </p>
                </div>
              ) : (
                <AssetsVsLiabilities
                  data={[dashboard_summary?.asset_vs_liability]}
                />
              )}
            </div>
          )}

          {element.i === 'Expenditure_vs_income' && (
            <div className={styles['grid_cardBottomChart']}>
              {!Boolean(
                Number(dashboard_summary?.income_vs_expense_bar_chart?.income),
              ) &&
              !Boolean(
                Number(dashboard_summary?.income_vs_expense_bar_chart?.expenses),
              ) ? (
                <div className={styles['infoEmptyState']}>
                  <Icon icon={'barChart'} width={40} height={40} />
                  <p className={styles['infoEmptyStateText']}>
                    {tr('No entries yet!')}
                  </p>
                </div>
              ) : (
                <IncomeExpenseBarChart
                  data={[dashboard_summary?.income_vs_expense_bar_chart]}
                />
              )}
            </div>
          )}

          {element.i === 'Line_chart' && (
            <div className={styles['grid_graph']}>
              <LazyLoad height={300} once>
                <IncomeVsExpenditure
                  dataList_1_year={dashboard_summary?.income_expense_payload}
                  incomeCategoryList_1_year={
                    dashboard_summary?.income_category_payload
                  }
                  expenseCategoryList_1_year={
                    dashboard_summary?.expense_category_payload
                  }
                  dataList_3_year={
                    dashboard_summary?.income_expense_payload_3_year_interval
                  }
                  incomeCategoryList_3_year={
                    dashboard_summary?.income_category_payload_3_year_interval
                  }
                  expenseCategoryList_3_year={
                    dashboard_summary?.expense_category_payload_3_year_interval
                  }
                  dataList_5_year={
                    dashboard_summary?.income_expense_payload_5_year_interval
                  }
                  incomeCategoryList_5_year={
                    dashboard_summary?.income_category_payload_5_year_interval
                  }
                  expenseCategoryList_5_year={
                    dashboard_summary?.expense_category_payload_5_year_interval
                  }
                  dataList_10_year={
                    dashboard_summary?.income_expense_payload_10_year_interval
                  }
                  incomeCategoryList_10_year={
                    dashboard_summary?.income_category_payload_10_year_interval
                  }
                  expenseCategoryList_10_year={
                    dashboard_summary?.expense_category_payload_10_year_interval
                  }
                />
              </LazyLoad>
            </div>
          )}

          {element.i === 'Asset_liability_Line_chart' && (
            <div className={styles['grid_graph']}>
              <LazyLoad height={300} once>
                <AssetLiabilityLineChart
                  dataList_1_year={dashboard_summary?.asset_liability_payload}
                  assetCategoryList_1_year={
                    dashboard_summary?.asset_category_payload
                  }
                  liabilityCategoryList_1_year={
                    dashboard_summary?.liability_category_payload
                  }
                  dataList_3_year={
                    dashboard_summary?.asset_liability_payload_3_year_interval
                  }
                  assetCategoryList_3_year={
                    dashboard_summary?.asset_category_payload_3_year_interval
                  }
                  liabilityCategoryList_3_year={
                    dashboard_summary?.liability_category_payload_3_year_interval
                  }
                  dataList_5_year={
                    dashboard_summary?.asset_liability_payload_5_year_interval
                  }
                  assetCategoryList_5_year={
                    dashboard_summary?.asset_category_payload_5_year_interval
                  }
                  liabilityCategoryList_5_year={
                    dashboard_summary?.liability_category_payload_5_year_interval
                  }
                  dataList_10_year={
                    dashboard_summary?.asset_liability_payload_10_year_interval
                  }
                  assetCategoryList_10_year={
                    dashboard_summary?.asset_category_payload_10_year_interval
                  }
                  liabilityCategoryList_10_year={
                    dashboard_summary?.liability_category_payload_10_year_interval
                  }
                />
              </LazyLoad>
            </div>
          )}

          {element.i === 'Donut_chart' && (
            <div className={styles['grid_graph_donut']}>
              {!dashboard_summary?.expense_chart_payload.length === 0 ||
              !dashboard_summary?.income_chart_payload.length === 0 ? (
                <LazyLoad height={300} once>
                  <ExpenseChart
                    dataList_1_year={
                      dashboard_summary?.expense_chart_payload_1_years_interval
                    }
                    dataList_3_year={
                      dashboard_summary?.expense_chart_payload_3_years_interval
                    }
                    dataList_5_year={
                      dashboard_summary?.expense_chart_payload_5_years_interval
                    }
                    dataList_100_year={dashboard_summary?.expense_chart_payload}
                    income_dataList_100_year={
                      dashboard_summary?.income_chart_payload
                    }
                    income_dataList_1_year={
                      dashboard_summary?.income_chart_payload_1_years_interval
                    }
                    income_dataList_3_year={
                      dashboard_summary?.income_chart_payload_3_years_interval
                    }
                    income_dataList_5_year={
                      dashboard_summary?.income_chart_payload_5_years_interval
                    }
                  />
                </LazyLoad>
              ) : (
                <div className={styles['infoEmptyState']}>
                  <Icon icon={'pieChart'} height={150} width={150} />

                  <p className={styles['infoEmptyStateText']}>
                    {tr('No entries yet!')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <ClickAwayListener
        onClickAway={() => {
          setShowSetting(false);
        }}
      >
        <div className={styles['settingsContainer']}>
          <div onClick={() => setShowSetting(!showSetting)}>
            <Icon icon="settingsIcon" width={30} height={30} />
          </div>

          {showSetting && (
            <div className={styles['settingOptionsContainer']}>
              <label className={styles['dropdown']}>
                {tr('Email')}
                <input
                  type="checkbox"
                  id="Email"
                  name="Email"
                  value={'Email'}
                  checked={showElements?.Email}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Liabilities')}
                <input
                  type="checkbox"
                  id="Liabilities"
                  name="Liabilities"
                  value={'Liabilities'}
                  checked={showElements?.Liabilities}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Assets')}
                <input
                  type="checkbox"
                  id="Assets"
                  name="Assets"
                  value={'Assets'}
                  checked={showElements?.Assets}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Documents')}
                <input
                  type="checkbox"
                  id="Documents"
                  name="Documents"
                  value={'Documents'}
                  checked={showElements?.Documents}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Space Group')}
                <input
                  type="checkbox"
                  id="Space_group"
                  name="Space_group"
                  value={'Space_group'}
                  checked={showElements?.Space_group}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Storage Space')}
                <input
                  type="checkbox"
                  id="Storage"
                  name="Storage"
                  value={'Storage'}
                  checked={showElements?.Storage}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Contracts (cost per month)')}
                <input
                  type="checkbox"
                  id="Top_5_contracts"
                  name="Top_5_contracts"
                  value={'Top_5_contracts'}
                  checked={showElements?.Top_5_contracts}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Notifications')}
                <input
                  type="checkbox"
                  id="contracts_document_notification"
                  name="contracts_document_notification"
                  value={'contracts_document_notification'}
                  checked={showElements?.contracts_document_notification}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Income vs. Expenses')}
                <input
                  type="checkbox"
                  id="Expenditure_vs_income"
                  name="Expenditure_vs_income"
                  value={'Expenditure_vs_income'}
                  checked={showElements?.Expenditure_vs_income}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Assets vs. Liabilities')}
                <input
                  type="checkbox"
                  id="Liabilities_VS_assets"
                  name="Liabilities_VS_assets"
                  value={'Liabilities_VS_assets'}
                  checked={showElements?.Liabilities_VS_assets}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Income & Expenses Line Chart')}
                <input
                  type="checkbox"
                  id="Line_chart"
                  name="Line_chart"
                  value={'Line_chart'}
                  checked={showElements?.Line_chart}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('assets & liabilities line chart')}
                <input
                  type="checkbox"
                  id="Asset_liability_Line_chart"
                  name="Asset_liability_Line_chart"
                  value={'Asset_liability_Line_chart'}
                  checked={showElements?.Asset_liability_Line_chart}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>

              <label className={styles['dropdown']}>
                {tr('Donut Chart')}
                <input
                  type="checkbox"
                  id="Donut_chart"
                  name="Donut_chart"
                  value={'Donut_chart'}
                  checked={showElements?.Donut_chart}
                  onChange={handleOnChangeElements}
                />
                <span className={styles['checkmark']}></span>
              </label>
            </div>
          )}
        </div>
      </ClickAwayListener>

      <ResponsiveGridLayout
        // layouts={{ lg: layout }}
        layouts={{ lg: layoutState }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        // breakpoints={{ lg: 996, md: 768, sm: 280 }}
        cols={{ lg: 6, md: 4, sm: 2, xs: 1 }}
        isResizable={false}
        margin={[10, 20]}
        compactType={'vertical'}
        rowHeight={300}
        allowOverlap={false}
        width={1000}
        onLayoutChange={handleLayoutChange}
        draggableCancel=".non-draggable"
      >
        {layoutState?.map((el) => createElement(el))}
        {/* {layout?.map((el) => createElement(el))} */}
      </ResponsiveGridLayout>
    </div>
  );
};

export default compose(withTranslation, withUserPreferences)(Grids);
